import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, FormGroup, FormControl, ControlLabel } from 'react-bootstrap';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import qs from 'qs';

import Spinner from '../../../../../../components/Loaders/Spinner';
import Alert from '../../../../../../components/Alert';
import FieldsName from './fields/Name';
import FieldsOptions from './fields/Options';
import FieldsCommission from './fields/Commission';
import FieldsInstructions from './fields/Instructions';

import { prepareForm, updateFormValues } from '../../../helpers';

const EditModal = ({ access, open, error, isLoading, isLoadingSubmit, isLoadingData, modalData, fetchPaymentSU, onHide, onShow, onSubmit }) => {
  const { default_language, languages, item, showData, fieldsToUpdate } = modalData;

  const formNode = useRef(null);

  const [isOpenTranslations, setOpenTranslations] = useState(false);
  const [isOpenInstructions, setOpenInstructions] = useState(false);
  const [instructionsState, setInstructionsState] = useState({});
  const [showCommission, setShowCommission] = useState(false);
  const [fixedPrice, setFixedPrice] = useState('');
  const [fixedPercent, setFixedPercent] = useState('');
  const [feeFromUser, setFeeFromUser] = useState('');
  const [amountsType, setAmountsType] = useState('0');
  const [modalError, setError] = useState(error);

  const onBlurFixedPrice = (e) => {
    const newTarget = e;
    if (e.target.value) {
      newTarget.target.value = parseFloat(e.target.value);
      setFixedPrice(newTarget.target.value);
    }
  };

  const onChangeFixedPrice = (event) => {
    const value = event.target.value.replace(/[,,]/, '.');
    if (value === '' || value === '00') {
      setFixedPrice('0');
    } else if (/^0[0-9].*$/.test(value)) {
      const newValue = value.replace(/^0+/, '');
      setFixedPrice(newValue);
    } else {
      const newValue = value.replace(',', '.');
      if (/^[0-9]*(?:\.|\.[0-9]{0,2})?$/.test(newValue)) {
        setFixedPrice(newValue);
      }
    }
  };

  const onChangeFixedPercent = (e) => {
    const { value } = e.target;
    if (!value) {
      return setFixedPercent('0');
    }
    if (/^\d{0,3}$/.test(value)) {
      return setFixedPercent(String(Math.min(200, parseInt(value, 10))));
    }
  };

  const onChangeInstruction = (code, value) => {
    setInstructionsState((prevState) => ({ ...prevState, [code]: value }));
  };

  const onSubmitForm = (event, hideModal = true) => {
    event?.preventDefault();
    const current = formNode?.current;
    const data = new FormData(current);
    const payload = {};
    for (const name of data.keys()) {
      if (name.startsWith('typeVideo')) {
        data.delete(name);
        continue;
      }
      payload[name] = formNode.current[name].value;
    }

    const parsePayload = qs.parse(payload);
    if (parsePayload?.instructions) {
      parsePayload.instructions = {
        ...parsePayload.instructions,
        ...instructionsState,
      };
      for (const key in instructionsState) {
        if (Object.hasOwn(instructionsState, key)) {
          data.set(`instructions[${key}]`, instructionsState[key]);
        }
      }
    }
    if (parsePayload?.new_users === '1') {
      data.set('new_users', '1');
      parsePayload.new_users = '1';
    }
    if (parsePayload?.new_users === '0') {
      data.set('new_users', '0');
      parsePayload.new_users = '0';
    }
    if (parsePayload?.take_fee_from_user === '1') {
      data.set('take_fee_from_user', '1');
      parsePayload.take_fee_from_user = '1';
    }
    if (parsePayload?.take_fee_from_user === '0') {
      data.set('take_fee_from_user', '0');
      parsePayload.take_fee_from_user = '0';
    }
    if (item?.details?.has_commission) {
      if (parsePayload?.extra_fee === 'on') {
        data.set('extra_fee', '1');
        parsePayload.extra_fee = '1';
      } else {
        data.set('extra_fee', '0');
        parsePayload.extra_fee = '0';
      }
      if (item?.details?.charge_fee_after_payment) {
        data.set('extra_fee', String(Number(item?.details?.extra_fee_enabled)));
        parsePayload.extra_fee = String(Number(item?.details?.extra_fee_enabled));

        if (parsePayload?.charge_fee_after_payment === 'on') {
          data.set('charge_fee_after_payment', '1');
          parsePayload.charge_fee_after_payment = '1';
        } else {
          data.set('charge_fee_after_payment', '0');
          parsePayload.charge_fee_after_payment = '0';
        }
      }
    }
    if (item?.details?.options?.find((opt) => opt.code === 'naming')) {
      if (parsePayload?.options?.naming === 'true') {
        data.set('options[naming]', '1');
        parsePayload.options.naming = '1';
      } else {
        data.set('options[naming]', 'false');
        parsePayload.options.naming = '0';
      }
    }
    if (item?.details?.options?.find((opt) => opt.code === 'test_mode')) {
      if (parsePayload?.options?.test_mode === 'true') {
        data.set('options[test_mode]', '1');
        parsePayload.options.test_mode = '1';
      } else {
        data.set('options[test_mode]', '0');
        parsePayload.options.test_mode = '0';
      }
    }
    if (item?.details?.options?.find((opt) => opt.code === 'billing_address_required')) {
      if (parsePayload?.options?.billing_address_required === 'true') {
        data.set('options[billing_address_required]', '1');
        parsePayload.options.billing_address_required = '1';
      } else {
        data.set('options[billing_address_required]', '0');
        parsePayload.options.billing_address_required = '0';
      }
    }
    if (access?.payments_get) {
      if (parsePayload?.show_data === 'true') {
        data.set('show_data', '1');
        parsePayload.show_data = '1';
      } else {
        data.set('show_data', '0');
        parsePayload.show_data = '0';
      }
    }
    onSubmit?.('edit', data.has('verification_file') || data.has('file') ? data : prepareForm(data, item), hideModal);
  };

  const onShowModal = () => {
    setError('');
    onShow?.();
  };

  const onExiting = () => {
    setOpenTranslations(false);
    setOpenInstructions(false);
    setShowCommission(false);
    setAmountsType('0');
  };

  const onEnter = () => {
    if (item?.details?.has_commission) {
      setShowCommission(item?.details?.charge_fee_after_payment ? item?.details?.charge_fee_after_payment_enabled : item?.details?.extra_fee_enabled);
      setFixedPrice(item?.details?.price_fixed || '0');
      setFixedPercent(item?.details?.price_percent || '0');
      if (item?.details?.is_enabled_take_fee_from_user) {
        setFeeFromUser(String(Number(item?.details?.take_fee_from_user)));
      }
    }
  };

  const onChangeFeeFromUser = (e) => {
    setFeeFromUser(e.target.value);
  };

  const onChangeOptAmount = (e) => {
    setAmountsType(e.target.value);
  };

  useEffect(() => {
    setError(error);
  }, [error]);

  useEffect(() => {
    if (showData) {
      if (formNode?.current?.show_data) {
        formNode.current.show_data.value = false;
      }
      updateFormValues(fieldsToUpdate, formNode?.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showData]);

  return (
    <Modal show={open} onHide={onHide} onExiting={() => onExiting()} onShow={onShowModal} onEnter={() => onEnter()} backdrop="static">
      {isLoading && <div className="modal-loader-small" />}
      <Modal.Header closeButton>
        <Modal.Title>
          {item?.method_name}
          {' '}
          (ID:
          {item?.id}
          )
        </Modal.Title>
      </Modal.Header>
      <form ref={formNode} onSubmit={onSubmitForm} className="payments-edit">
        <Modal.Body>
          {modalError && <Alert text={modalError} />}

          <FieldsName
            isOpenTranslations={isOpenTranslations}
            item={item}
            defaultLanguage={default_language}
            languages={languages}
            setOpenTranslations={setOpenTranslations}
          />
          {!item?.details?.manual ? (
            <>
              <FormGroup className={classNames('', { hidden: amountsType === '1' })}>
                <ControlLabel htmlFor="minimal">Minimal payment</ControlLabel>
                <FormControl type="text" id="minimal" name="minimal" defaultValue={item?.min || ''} />
              </FormGroup>
              <FormGroup className={classNames('', { hidden: amountsType === '1' })}>
                <ControlLabel htmlFor="maximal">Maximal payment</ControlLabel>
                <FormControl type="text" id="maximal" name="maximal" defaultValue={item?.max || ''} />
              </FormGroup>
            </>
          ) : null}
          <FormGroup>
            <ControlLabel htmlFor="new_users">New users</ControlLabel>
            <FormControl id="new_users" name="new_users" componentClass="select" placeholder="Select" defaultValue={String(Number(item?.new_users))}>
              <option value="1">Allowed</option>
              <option value="0">Not allowed</option>
            </FormControl>
          </FormGroup>
          {item?.details?.is_enabled_take_fee_from_user ? (
            <FormGroup>
              <ControlLabel htmlFor="take_fee_from_user">{item?.details?.take_fee_from_user_title}</ControlLabel>
              <FormControl
                id="take_fee_from_user"
                name="take_fee_from_user"
                componentClass="select"
                placeholder="Select"
                defaultValue={String(Number(item?.details?.take_fee_from_user))}
                onChange={(e) => onChangeFeeFromUser(e)}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </FormControl>
            </FormGroup>
          ) : null}
          {item?.details?.addfunds_amount_currencies?.length ? (
            <FormGroup>
              <ControlLabel htmlFor="amount_currency">Checkout currency</ControlLabel>
              <FormControl
                id="amount_currency"
                name="amount_currency"
                componentClass="select"
                placeholder="Select"
                defaultValue={item?.details?.amount_currency}
              >
                {item?.details?.addfunds_amount_currencies?.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          ) : null}
          <FieldsCommission
            item={item}
            feeFromUser={feeFromUser}
            showCommission={showCommission}
            fixedPrice={fixedPrice}
            fixedPercent={fixedPercent}
            onBlurFixedPrice={onBlurFixedPrice}
            onChangeFixedPrice={onChangeFixedPrice}
            onChangeFixedPercent={onChangeFixedPercent}
            setShowCommission={setShowCommission}
          />
          {item?.details?.description ? (
            <>
              <hr />
              <div dangerouslySetInnerHTML={{ __html: item?.details?.description }} />
            </>
          ) : null}
          <hr />
          {item?.details?.options?.length ? (
            <FieldsOptions
              access={access}
              isLoadingSubmit={isLoadingSubmit}
              isLoadingData={isLoadingData}
              error={error}
              item={item}
              formNode={formNode}
              amountsType={amountsType}
              showData={showData}
              onShowData={fetchPaymentSU}
              onChangeOptAmount={onChangeOptAmount}
              onSubmitForm={onSubmitForm}
            />
          ) : null}
          <FieldsInstructions
            isOpenInstructions={isOpenInstructions}
            item={item}
            languages={languages}
            defaultLanguage={default_language}
            onChange={onChangeInstruction}
            setOpenInstructions={setOpenInstructions}
          />
        </Modal.Body>
        <Modal.Footer className="custom__modal-footer">
          <Button bsStyle="primary" type="submit" disabled={isLoadingSubmit}>
            {isLoadingSubmit && <Spinner />}
            <span>Save changes</span>
          </Button>
          <Button
            onClick={() => {
              onHide();
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

EditModal.propTypes = {
  access: PropTypes.object,
  error: PropTypes.string,
  open: PropTypes.bool,
  isLoading: PropTypes.bool,
  isLoadingSubmit: PropTypes.bool,
  isLoadingData: PropTypes.bool,
  modalData: PropTypes.object,
  fetchPaymentSU: PropTypes.func,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditModal;
